import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, CheckCircle2, MessageCircle } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings, waUrl } from "@/context/SettingsContext";
import AiWriteButton from "@/components/AiWriteButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = "w-full rounded-xl border border-brand/20 bg-white px-4 py-3.5 text-sm text-forest placeholder:text-brand/40 outline-none transition-all duration-300 focus:border-gold focus:ring-4 focus:ring-gold/10";
const labelClass = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-brand";

const QuestionForm = () => {
  const { lang, t } = useLanguage();
  const { settings: st } = useSettings();
  const f = t.contact.form;
  const empty = { name: "", phone: "", email: "", subject: "", message: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone.trim() && !form.email.trim()) { toast.error(f.contactRequired); return; }
    setSending(true);
    try {
      await axios.post(`${API}/questions`, { ...form, lang });
      toast.success(f.questionSuccess);
      setSent(form);
      setForm(empty);
    } catch (err) {
      toast.error(err.response?.data?.detail || f.error);
    } finally { setSending(false); }
  };

  if (sent) {
    const text = `${f.subject}: ${sent.subject}\n${f.name}: ${sent.name}\n\n${sent.message}`;
    return (
      <div className="flex flex-col items-start gap-6 rounded-3xl border border-brand/10 bg-white p-8 shadow-xl shadow-brand/5 sm:p-12" data-testid="question-sent-panel">
        <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-mint text-brand"><CheckCircle2 className="h-8 w-8" /></span>
        <h2 className="font-display text-2xl font-bold text-forest sm:text-3xl">{f.questionSuccess}</h2>
        <p className="text-base text-brand-hover">{f.whatsappHint}</p>
        <div className="flex flex-wrap gap-3">
          <a href={`${waUrl(st)}?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer" data-testid="question-whatsapp-also-button" className="inline-flex items-center gap-3 rounded-full bg-brand px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-lg shadow-brand/25 transition-all hover:-translate-y-0.5 hover:bg-brand-hover"><MessageCircle className="h-4 w-4" />{f.whatsappAlso}</a>
          <button onClick={() => setSent(null)} data-testid="question-new-button" className="inline-flex items-center gap-2 rounded-full border-2 border-gold px-7 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-all hover:bg-gold hover:text-white">{f.newRequest}</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5 rounded-3xl border border-brand/10 bg-white p-6 shadow-xl shadow-brand/5 sm:p-10" data-testid="question-form">
      <div>
        <label className={labelClass}>{f.name} *</label>
        <input required value={form.name} onChange={set("name")} placeholder={f.namePh} data-testid="question-form-name-input" className={inputClass} />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelClass}>{f.phoneSimple} *</label>
          <input required value={form.phone} onChange={set("phone")} placeholder={f.phonePh} data-testid="question-form-phone-input" className={inputClass} />
        </div>
        <div>
          <label className={labelClass}>{f.emailSimple}</label>
          <input type="email" value={form.email} onChange={set("email")} placeholder={f.emailPh} data-testid="question-form-email-input" className={inputClass} />
        </div>
      </div>
      <p className="-mt-2 text-xs text-brand-hover">{f.contactRequired}</p>
      <div>
        <label className={labelClass}>{f.subject} *</label>
        <input required maxLength={200} value={form.subject} onChange={set("subject")} placeholder={f.subjectPh} data-testid="question-form-subject-input" className={inputClass} />
      </div>
      <div>
        <label className={labelClass}>{f.questionMessage} *</label>
        <textarea required rows={5} value={form.message} onChange={set("message")} placeholder={f.questionMessagePh} data-testid="question-form-message-input" className={`${inputClass} resize-none`} />
        <AiWriteButton kind="question" value={form.message} onText={(message) => setForm((p) => ({ ...p, message }))} context={{ sujet: form.subject }} testid="question-ai-write" />
      </div>
      <button type="submit" disabled={sending} data-testid="question-form-submit-button" className="group flex w-full items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white shadow-xl shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60 sm:w-auto">
        {sending ? f.sending : f.submitQuestion}<Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};

export default QuestionForm;
